/**
 * Google Identity Services — shared types and script loader.
 *
 * Used by GoogleSignIn (web auth) and AccountDeleteView (public account
 * deletion). Both need to load the GIS script and call
 * `google.accounts.id.initialize` / `renderButton`. Extracting the types
 * and loader here avoids duplicate `declare global` declarations that
 * would need to be kept compatible.
 */

export type CredentialResponse = { credential: string; select_by?: string };

export interface GoogleAccountsId {
  initialize: (config: {
    client_id: string;
    callback: (resp: CredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }) => void;
  renderButton: (
    parent: HTMLElement,
    options: {
      theme?: 'outline' | 'filled_blue' | 'filled_black';
      size?: 'large' | 'medium' | 'small';
      shape?: 'rectangular' | 'pill' | 'circle' | 'square';
      text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
      width?: number;
      locale?: string;
    },
  ) => void;
  prompt?: () => void;
}

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: GoogleAccountsId;
      };
    };
  }
}

const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

/**
 * Loads the Google Identity Services script exactly once and resolves
 * with the `google.accounts.id` client. Safe to call from multiple
 * components — the script tag is shared and dataset flags prevent
 * hanging promises on retry after a load failure.
 */
export function loadGis(): Promise<GoogleAccountsId> {
  const { promise, resolve, reject } =
    Promise.withResolvers<GoogleAccountsId>();

  if (window.google?.accounts?.id) {
    resolve(window.google.accounts.id);
    return promise;
  }

  const existing = document.querySelector<HTMLScriptElement>(
    `script[src="${GIS_SCRIPT_SRC}"]`,
  );
  if (existing) {
    const loaded = existing.dataset.loaded === 'true';
    const errored = existing.dataset.error === 'true';
    if (loaded && window.google?.accounts?.id) {
      resolve(window.google.accounts.id);
    } else if (errored) {
      reject(new Error('GIS script failed to load'));
    } else {
      existing.addEventListener('load', () => {
        if (window.google?.accounts?.id) resolve(window.google.accounts.id);
        else reject(new Error('GIS loaded but google.accounts.id missing'));
      });
      existing.addEventListener('error', () =>
        reject(new Error('GIS script failed to load')),
      );
    }
    return promise;
  }

  const script = document.createElement('script');
  script.src = GIS_SCRIPT_SRC;
  script.async = true;
  script.defer = true;
  script.onload = () => {
    script.dataset.loaded = 'true';
    if (window.google?.accounts?.id) resolve(window.google.accounts.id);
    else reject(new Error('GIS loaded but google.accounts.id missing'));
  };
  script.onerror = () => {
    script.dataset.error = 'true';
    reject(new Error('GIS script failed to load'));
  };
  document.head.appendChild(script);
  return promise;
}
